// Two HTML pages generated on an SOR payment, alongside the advice CSV:
//   brandPageHtml   — "For <Brand> - <date>": what's being paid, per shipment, with thumbnails.
//   recordsPageHtml — "For Skipzy Records <Brand> - <date>": per shipment, paid before /
//                     paid today / yet to be paid, with every piece listed.
// Amounts are cost incl GST (what the brand is paid). No selling prices on the brand page.
import { cldThumb } from './img';

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const ddmmyyyy = (iso) => { if (!iso) return '—'; const [y, m, d] = iso.slice(0, 10).split('-'); return `${d}/${m}/${y}`; };
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const incl = (it) => Number(it.cost_price_with_tax ?? it.cost_price ?? 0);
const excl = (it) => Number(it.cost_price ?? 0);
const sizeLabel = (it) => (it.brand_size && it.brand_size !== it.size ? `${esc(it.brand_size)} (${esc(it.size)})` : esc(it.size || '—'));
const thumb = (it) => (it.image_url_1 ? `<img src="${esc(cldThumb(it.image_url_1, 300))}" loading="lazy">` : '<div class="noimg">No image</div>');
const byShipment = (items) => {
  const m = {};
  items.forEach((it) => { (m[it.sor_start_date || ''] = m[it.sor_start_date || ''] || []).push(it); });
  return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
};

const STYLE = `body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;margin:24px;color:#1f2937;background:#fff}
h1{font-size:22px;margin:0 0 4px}h2{font-size:16px;margin:28px 0 8px;padding-bottom:6px;border-bottom:2px solid #25D366}
.muted{color:#6b7280;font-size:13px}.cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:10px;margin:16px 0}
.card{border:1px solid #e5e7eb;border-radius:10px;padding:10px 12px}.card b{display:block;font-size:18px}.card span{font-size:11px;color:#6b7280}
.big b{font-size:24px;color:#15803d}.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:12px}
.item{border:1px solid #e5e7eb;border-radius:10px;padding:8px;font-size:12px}.item img,.noimg{width:100%;aspect-ratio:1;object-fit:cover;border-radius:8px;background:#f3f4f6}
.noimg{display:flex;align-items:center;justify-content:center;color:#9ca3af}.nm{font-weight:600;margin:6px 0 2px;font-size:12px}
.bc{font-family:ui-monospace,monospace;font-weight:700}.zb{font-family:ui-monospace,monospace;color:#6b7280;font-size:11px}
.amt{font-weight:700;color:#15803d;margin-top:4px}table{border-collapse:collapse;width:100%;font-size:12px;margin:8px 0}
th,td{border-bottom:1px solid #eee;padding:6px 8px;text-align:left}th{background:#f9fafb;color:#6b7280;font-weight:600}
td.r,th.r{text-align:right}tr.tot td{font-weight:700;border-top:2px solid #d1d5db}tr.sub td{color:#6b7280}tr.sub td:first-child{padding-left:24px}
td img{width:40px;height:40px;object-fit:cover;border-radius:6px;background:#f3f4f6}.pill{display:inline-block;border-radius:999px;padding:1px 8px;font-size:11px;background:#f3f4f6}
.today{background:#dcfce7;color:#166534}.owed{background:#fef3c7;color:#92400e}.prev{background:#e0f2fe;color:#075985}.gone{background:#fee2e2;color:#991b1b}
@media print{body{margin:10mm}.item{break-inside:avoid}}`;

export function brandPageHtml(advice, paidItems) {
  const totIncl = paidItems.reduce((s, it) => s + incl(it), 0);
  const totExcl = paidItems.reduce((s, it) => s + excl(it), 0);
  const sold = paidItems.map((it) => it.sold_date).filter(Boolean).sort();
  const groups = byShipment(paidItems);
  const sections = groups.map(([ship, its]) => `
    <h2>Shipment of ${ddmmyyyy(ship)} — ${its.length} piece${its.length !== 1 ? 's' : ''} — ${inr(its.reduce((s, it) => s + incl(it), 0))}</h2>
    <div class="grid">${its.sort((a, b) => (a.sold_date || '').localeCompare(b.sold_date || '')).map((it) => `
      <div class="item">${thumb(it)}
        <div class="nm">${esc(it.design_name || '')}</div>
        <div class="bc">${esc(it.brand_barcode || '—')}</div>
        <div class="zb">Zoddle ${esc(it.zoddle_barcode)}</div>
        <div>Size ${sizeLabel(it)}</div>
        <div>Sold ${ddmmyyyy(it.sold_date)}</div>
        <div class="amt">${inr(incl(it))}</div>
      </div>`).join('')}</div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>For ${esc(advice.brand)} - ${ddmmyyyy(advice.export_date)}</title><style>${STYLE}</style></head><body>
  <h1>Payment to ${esc(advice.brand)}</h1>
  <div class="muted">Skipzy (Zoddle Lifestyle Pvt Ltd) · Sale-or-Return settlement · Advice Ref <b>${esc(advice.reference)}</b></div>
  <div class="cards">
    <div class="card big"><span>Total being paid (incl GST)</span><b>${inr(totIncl)}</b></div>
    <div class="card"><span>Amount excl GST</span><b>${inr(totExcl)}</b></div>
    <div class="card"><span>GST</span><b>${inr(totIncl - totExcl)}</b></div>
    <div class="card"><span>Pieces paid for</span><b>${paidItems.length}</b></div>
    <div class="card"><span>Shipments covered</span><b>${groups.length}</b></div>
    <div class="card"><span>Pieces sold between</span><b>${sold.length ? `${ddmmyyyy(sold[0])} – ${ddmmyyyy(sold[sold.length - 1])}` : '—'}</b></div>
    <div class="card"><span>Payment date</span><b>${ddmmyyyy(advice.export_date)}</b></div>
    <div class="card"><span>Previous payment</span><b>${ddmmyyyy(advice.last_payment_date)}</b></div>
  </div>
  <table><thead><tr><th>Shipment</th><th class="r">Pieces</th><th class="r">Amount (incl GST)</th></tr></thead><tbody>
    ${groups.map(([ship, its]) => `<tr><td>${ddmmyyyy(ship)}</td><td class="r">${its.length}</td><td class="r">${inr(its.reduce((s, it) => s + incl(it), 0))}</td></tr>`).join('')}
    <tr class="tot"><td>Total</td><td class="r">${paidItems.length}</td><td class="r">${inr(totIncl)}</td></tr></tbody></table>
  ${sections}
  </body></html>`;
}

// One bucket per piece, from its post-payment state. Anything not paid, sold,
// returned or written off is still physically with us -> "In stock".
const BUCKETS = [
  { key: 'prev',     label: 'Paid previously',              pill: 'prev' },
  { key: 'today',    label: 'Paid today',                   pill: 'today' },
  { key: 'sold',     label: 'Sold, not yet paid',           pill: 'owed', owed: true },
  { key: 'stock',    label: 'In stock (unsold)',            pill: 'owed', owed: true },
  { key: 'missing',  label: 'Missing',                      pill: 'owed', owed: true },
  { key: 'damaged',  label: 'Damaged',                      pill: 'owed', owed: true },
  { key: 'gifted',   label: 'Gifted / non-revenue',         pill: 'owed', owed: true },
  { key: 'returned', label: 'Returned to brand (not payable)', pill: 'gone' },
];
function bucketOf(it, paidToday) {
  if (paidToday.has(it.item_id)) return 'today';
  if (it.paid_to_brand) return 'prev';
  if (it.status === 'sold') return 'sold';
  if (it.status === 'returned_to_brand') return 'returned';
  if (it.status === 'missing') return 'missing';
  if (it.status === 'damaged') return 'damaged';
  if (it.status === 'gifted' || it.status === 'non_revenue') return 'gifted';
  return 'stock';
}

function bucketTable(items, paidToday) {
  const agg = {};
  BUCKETS.forEach((b) => { agg[b.key] = { n: 0, v: 0 }; });
  items.forEach((it) => { const a = agg[bucketOf(it, paidToday)]; a.n += 1; a.v += incl(it); });
  const owed = BUCKETS.filter((b) => b.owed);
  const owedN = owed.reduce((s, b) => s + agg[b.key].n, 0);
  const owedV = owed.reduce((s, b) => s + agg[b.key].v, 0);
  const row = (label, a, cls = '') => `<tr class="${cls}"><td>${label}</td><td class="r">${a.n}</td><td class="r">${inr(a.v)}</td></tr>`;
  return `<table><thead><tr><th>Status</th><th class="r">Pieces</th><th class="r">Value (cost incl GST)</th></tr></thead><tbody>
    ${row('Received in shipment', { n: items.length, v: items.reduce((s, it) => s + incl(it), 0) }, 'tot')}
    ${row('Paid previously', agg.prev)}${row('Paid today', agg.today)}
    ${row('Yet to be paid', { n: owedN, v: owedV }, 'tot')}
    ${owed.map((b) => row(b.label, agg[b.key], 'sub')).join('')}
    ${row('Returned to brand (not payable)', agg.returned)}</tbody></table>`;
}

export function recordsPageHtml(advice, shipmentItems, paidItemIds, advices) {
  const paidToday = new Set(paidItemIds);
  const groups = byShipment(shipmentItems);
  const today = new Date(advice.export_date);
  const sections = groups.map(([ship, its]) => {
    const end = its.find((it) => it.sor_end_date)?.sor_end_date;
    const left = end ? Math.round((new Date(end) - today) / 86400000) : null;
    const order = Object.fromEntries(BUCKETS.map((b, i) => [b.key, i]));
    const rows = its.slice().sort((a, b) => order[bucketOf(a, paidToday)] - order[bucketOf(b, paidToday)]
      || (a.zoddle_barcode || '').localeCompare(b.zoddle_barcode || ''));
    return `<h2>Shipment of ${ddmmyyyy(ship)} · SOR ends ${ddmmyyyy(end)}${left != null ? ` (${left >= 0 ? `${left} days left` : `ended ${-left} days ago`})` : ''}</h2>
      ${bucketTable(its, paidToday)}
      <table><thead><tr><th></th><th>Zoddle barcode</th><th>Brand barcode</th><th>Design</th><th>Size</th><th>Status</th><th>Sold</th><th>Paid on</th><th class="r">Cost incl GST</th></tr></thead><tbody>
      ${rows.map((it) => { const b = BUCKETS.find((x) => x.key === bucketOf(it, paidToday)); return `<tr>
        <td>${it.image_url_1 ? `<img src="${esc(cldThumb(it.image_url_1, 100))}" loading="lazy">` : ''}</td>
        <td class="bc">${esc(it.zoddle_barcode)}</td><td>${esc(it.brand_barcode || '—')}</td><td>${esc(it.design_name || '')}</td>
        <td>${sizeLabel(it)}</td><td><span class="pill ${b.pill}">${esc(b.label)}</span></td>
        <td>${ddmmyyyy(it.sold_date)}</td><td>${paidToday.has(it.item_id) ? ddmmyyyy(advice.export_date) : ddmmyyyy(it.paid_to_brand_at)}</td>
        <td class="r">${inr(incl(it))}</td></tr>`; }).join('')}</tbody></table>`;
  }).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>For Skipzy Records ${esc(advice.brand)} - ${ddmmyyyy(advice.export_date)}</title><style>${STYLE}</style></head><body>
  <h1>${esc(advice.brand)} — SOR payment record</h1>
  <div class="muted">For Skipzy records · Advice Ref <b>${esc(advice.reference)}</b> · paid ${ddmmyyyy(advice.export_date)} · previous payment ${ddmmyyyy(advice.last_payment_date)} · values are cost incl GST</div>
  <h2>All shipments</h2>${bucketTable(shipmentItems, paidToday)}
  <table><thead><tr><th>Shipment</th><th class="r">Received</th><th class="r">Paid previously</th><th class="r">Paid today</th><th class="r">Yet to be paid</th></tr></thead><tbody>
  ${groups.map(([ship, its]) => {
    const v = (keys) => its.filter((it) => keys.includes(bucketOf(it, paidToday))).reduce((s, it) => s + incl(it), 0);
    return `<tr><td>${ddmmyyyy(ship)}</td><td class="r">${inr(its.reduce((s, it) => s + incl(it), 0))}</td><td class="r">${inr(v(['prev']))}</td>
      <td class="r">${inr(v(['today']))}</td><td class="r">${inr(v(BUCKETS.filter((b) => b.owed).map((b) => b.key)))}</td></tr>`; }).join('')}</tbody></table>
  <h2>Payments to this brand</h2>
  <table><thead><tr><th>Advice Ref</th><th>Paid on</th><th class="r">Pieces</th><th class="r">Amount</th></tr></thead><tbody>
  ${(advices || []).map((a) => `<tr><td>${esc(a.reference)}</td><td>${ddmmyyyy(a.paid_at)}</td><td class="r">${a.units}</td><td class="r">${inr(a.total)}</td></tr>`).join('')}</tbody></table>
  ${sections}
  </body></html>`;
}
